/* eslint-disable no-console */
import clsx from "clsx";
import {ButtonAddTable} from "components/Button";
import {DatePicker} from "components/DatePicker";
import {DashboardLayout} from "components/Layout";
import {ModalConfirmation} from "components/Modal";
import {BasicTable, SearchBox} from "components/Table";
import statusColor from "config/patientStatus";
import dayjs from "dayjs";
import {useListAppoitments} from "hooks/AdminAdmission/Appoitment";
import {useEditAppoinment} from "hooks/Doctor/Appointment";
import {useStateCallback} from "hooks/useStateCallback";
import {useToast} from "hooks/useToast";
import {useCallback, useMemo, useState} from "react";
import {isMobile} from "react-device-detect";
import {BsThreeDotsVertical} from "react-icons/bs";
import {useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {convertErrorMessageFormat} from "utils/converter";
import {capitalizeFirstLetter} from "utils/string";
import {FaRegCalendarCheck} from "react-icons/fa6";
import {MdOutlineCancelPresentation} from "react-icons/md";
import {FaShareAlt} from "react-icons/fa";
import {MdOutlinePaid} from "react-icons/md";
import {
	useCancelAppoitment,
	usePaidAppoitment,
	useSharePaymentLinkAppoitment,
} from "hooks/AdminAdmission/Appoitment/useMutationAppoitment";
import {MdContentCopy} from "react-icons/md";

export function AppointmentListPage() {
	const history = useHistory();
	const {showToast} = useToast();

	const selectedHospital = useSelector(state => state.HospitalsReducer.selectedHospital);
	const [filter, setFilter] = useState({
		page: 1,
		search: "",
		limit: 10,
		idHospital: selectedHospital?.Hospital?.id,
		date: null,
		sortBy: "createdAt.desc",
	});
	const {data: appoitments, isLoading} = useListAppoitments({
		idConsutation: null,
		idHospital: filter.idHospital,
		limit: filter.limit,
		page: filter.page,
		search: filter.search,
		date: filter.date,
		sortBy: filter.sortBy,
	});

	const [showActionMobile, setShowActionMobile] = useState(null);
	const [selectedData, setSelectedData] = useStateCallback(null);
	const [showCheckInModal, setShowCheckInModal] = useState(false);

	const updateAppointmentMutation = useEditAppoinment();

	const handleCheckInAppointment = () => {
		updateAppointmentMutation.mutate(
			{
				id: selectedData.id,
				day: selectedData.day,
				date: selectedData.date,
				startTime: selectedData.startTime,
				endTime: selectedData.endTime,
				type: selectedData.type,
				patientStatus: "belum_dilayani",
			},
			{
				onSuccess: () => {
					showToast(
						"success",
						`Data Appointment pasien ${selectedData.patient.fullName} berhasil diperbarui`,
						3000,
					);
					window.scrollTo(0, 0);
					handleCloseCheckInModal();
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	const consultationTypeRenderer = type => {
		return (
			<span
				className={
					type === "offline"
						? "text-purple ft-18 icon-ico-hospital me-2"
						: "text-primary ft-18 icon-ico-zoom me-2"
				}
			/>
		);
	};

	const [isOpenCancel, setIsOpenCancel] = useState(false);
	const {mutate: mutateCancel, isLoading: isLoadingCancel} = useCancelAppoitment(selectedData?.id);
	const handleCancel = useCallback(() => {
		mutateCancel(
			{},
			{
				onSuccess: () => {
					showToast(
						"success",
						`Data Appointment pasien ${selectedData?.patient?.fullName} berhasil dicancel`,
						3000,
					);
					window.scrollTo(0, 0);
					setIsOpenCancel(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	}, [mutateCancel, selectedData, showToast]);

	const [isOpenPaid, setIsOpenPaid] = useState(false);
	const {mutate: mutatePaid, isLoading: isLoadingPaid} = usePaidAppoitment(selectedData?.id);
	const handlePaid = useCallback(() => {
		mutatePaid(
			{},
			{
				onSuccess: () => {
					showToast(
						"success",
						`Data Appointment pasien ${selectedData?.patient?.fullName} berhasil dibayar`,
						3000,
					);
					window.scrollTo(0, 0);
					setIsOpenPaid(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	}, [mutatePaid, selectedData, showToast]);

	const [isOpenSharePaymentLink, setIsOpenSharePaymentLink] = useState(false);
	const [shareTo, setShareTo] = useState([]);
	const changeShareTo = value => {
		if (shareTo.includes(value)) {
			setShareTo([...shareTo.filter(item => item !== value)]);
		} else {
			setShareTo([...shareTo, value]);
		}
	};
	const {mutate: mutateSharePaymentLink, isLoading: isLoadingSharePaymentLink} = useSharePaymentLinkAppoitment(
		selectedData?.id,
	);
	const handleSharePaymentLink = useCallback(() => {
		mutateSharePaymentLink(
			{channels: shareTo},
			{
				onSuccess: () => {
					showToast(
						"success",
						`Link Pembayaran Appointment pasien ${selectedData?.patient?.fullName} berhasil dikirim`,
						3000,
					);
					window.scrollTo(0, 0);
					setIsOpenSharePaymentLink(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	}, [mutateSharePaymentLink, selectedData, shareTo, showToast]);

	const columns = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
				width: 100,
				renderStyle({row}) {
					return {borderLeft: `8px solid ${statusColor.bgColor[row?.original?.patientStatus]}`};
				},
			},
			{
				Header: "Nama Pasien",
				accessor: "patientName",
				minWidth: 240,
				Cell: ({row}) => {
					return (
						<span>
							{consultationTypeRenderer(row?.original?.type)} {row?.original?.patient?.fullName}
						</span>
					);
				},
			},
			{
				Header: "Nama Dokter",
				accessor: "doctorName",
				minWidth: 240,
				Cell: ({row}) => {
					return row.original?.doctor?.firstName + " " + row.original?.doctor?.lastName;
				},
			},
			{
				Header: "Unit & Layanan",
				accessor: "unit",
				minWidth: 240,
				Cell: ({row}) => {
					return row.original?.doctor?.staff?.policlinic?.name || "-";
				},
			},
			{
				Header: "Jadwal",
				accessor: "schedule",
				minWidth: 240,
				Cell: ({row}) => {
					return (
						row.original.date && (
							<>
								<div>{`${dayjs(row.original.date).format("DD MMMM YYYY")}`}</div>
								<div>{`${row.original.startTime} - ${row.original.endTime}`}</div>
							</>
						)
					);
				},
			},
			{
				Header: "Tanggal Dibuat",
				accessor: "createdAt",
				minWidth: 240,
				Cell: ({row}) => {
					return (
						row.original.createdAt && (
							<>
								<div>{`${dayjs(row.original.createdAt).format("DD MMMM YYYY")}`}</div>
							</>
						)
					);
				},
			},
			{
				Header: "Status",
				accessor: "bookingStatus",
				Cell: ({row}) => {
					if (row?.original?.medicalRecord) {
						return <div className="box-status serve">Sudah dilayani</div>;
					}

					if (row?.original?.bookingStatus === "canceled") {
						return <div className="box-status not-serve">Dibatalkan</div>;
					}

					if (["pasien", "belum_dilayani", "sedang_dilayani"].includes(row?.original?.patientStatus)) {
						return <div className="box-status not-serve">Belum dilayani</div>;
					} else {
						return <div className="box-status serve">Sudah dilayani</div>;
					}
				},
			},
			// {
			// 	Header: "Status",
			// 	accessor: "visibility",
			// 	Cell: ({row}) => {
			// 		return row.original.paymentStatus && capitalizeFirstLetter(row.original.paymentStatus);
			// 	},
			// },

			{
				Header: "Action",
				accessor: "id",
				Cell: ({row}) => {
					return (
						<div className="d-flex flex-row  align-items-center" style={{minWidth: 150}}>
							<a
								class="text-orange wait-pay mr10"
								data-placement="top"
								data-toggle="tooltip"
								style={{cursor: "pointer"}}
								title="Edit data"
								onClick={() => history.push("/admission/appointment/detail/" + row.original.id)}>
								<span class="icon-ico-eye-open ft-18" />
							</a>

							{row?.original?.patientStatus === "pasien" && row?.original?.bookingStatus !== "canceled" && (
								<>
									<a
										class="text-orange wait-pay mr10"
										data-placement="top"
										data-toggle="tooltip"
										style={{cursor: "pointer"}}
										title="Edit data"
										onClick={() => history.push("/admission/appointment/edit/" + row.original.id)}>
										<span className="icon-ico-pencil ft-18" />
									</a>
									<MdOutlineCancelPresentation
										className="text-orange wait-pay mr10"
										fontSize={16}
										style={{marginTop: "-5px", cursor: "pointer"}}
										title="Cancel Appointment"
										onClick={() => {
											setSelectedData(row?.original);
											setIsOpenCancel(true);
										}}
									/>
									{row?.original?.type === "offline" && (
										<FaRegCalendarCheck
											className="text-orange wait-pay mr10"
											fontSize={16}
											style={{marginTop: "-5px", cursor: "pointer"}}
											title="Check in"
											onClick={() => {
												setSelectedData(row?.original);
												setShowCheckInModal(true);
											}}
										/>
									)}
									<FaShareAlt
										className="text-orange wait-pay mr10"
										fontSize={16}
										style={{marginTop: "-5px", cursor: "pointer"}}
										title="Share Link"
										onClick={() => {
											setSelectedData(row?.original);
											setIsOpenSharePaymentLink(true);
										}}
									/>
									<MdOutlinePaid
										className="text-orange wait-pay mr10"
										fontSize={16}
										style={{marginTop: "-5px", cursor: "pointer"}}
										title="Pay"
										onClick={() => {
											setSelectedData(row?.original);
											setIsOpenPaid(true);
										}}
									/>
									{/* {row?.original?.type === "offline" && (
										<button
											class="btn btn-outline-primary btn-checkin"
											type="button"
											onClick={() => {
												setSelectedData(row?.original, () => setShowCheckInModal(true));
											}}>
											Check-in
										</button>
									)} */}
								</>
							)}
						</div>
					);
				},
			},
		],
		[],
	);

	const columnsMobile = useMemo(
		() => [
			{
				Header: "Rumah Sakit",
				accessor: "hospital",
				Cell: ({row}) => {
					return (
						<div class="col-7">
							<div class="text-table-medium text-bold text-elipsis">
								{consultationTypeRenderer(row?.original?.type)} {row?.original?.patient?.fullName}
							</div>
							<div className="text-table-small text-grey text-elipsis">
								<div
									className="box-status "
									style={{
										background: statusColor.bgColor[row?.original?.patientStatus],
										color: "#fff",
									}}
								/>
							</div>
							<div
								class="text-grey text-table-small"
								style={{textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
								<div>{row.original?.doctor?.firstName + " " + row.original?.doctor?.lastName}</div>
								<div>{row.original.status && capitalizeFirstLetter(row.original.status)}</div>
							</div>
						</div>
					);
				},
			},
			{
				Header: "Schedule",
				accessor: "updatedAt",
				Cell: ({row}) => {
					return (
						<div class="col-4">
							{/* <div className="text-table-medium">{row.original.idUserDoctor}</div> */}
							<div
								class="text-grey text-table-small"
								style={{textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
								<div>{`${dayjs(row.original.date).format("DD MMMM YYYY")}`}</div>
							</div>
						</div>
					);
				},
			},

			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => {
					return (
						<div className="col-2">
							<div class="dropdown dropstart">
								<span
									style={{padding: "5px"}}
									onClick={() => {
										if (showActionMobile !== null) {
											setShowActionMobile(null);
										} else {
											setShowActionMobile(row.original.id);
										}
									}}>
									<BsThreeDotsVertical />
								</span>

								<ul class={clsx("dropdown-menu", showActionMobile === row.original.id && "show")}>
									{row?.original?.patientStatus === "pasien" &&
										row?.original?.bookingStatus !== "canceled" && (
											<>
												{row?.original?.type === "offline" && (
													<li>
														<button
															class="dropdown-item text-table-medium"
															data-placement="top"
															data-toggle="tooltip"
															style={{cursor: "pointer"}}
															title="Edit data"
															onClick={() => {
																setSelectedData(row?.original, () =>
																	setShowCheckInModal(true),
																);
															}}>
															Check-in
														</button>
													</li>
												)}

												<li>
													<a
														class="dropdown-item text-table-medium"
														data-placement="top"
														data-toggle="tooltip"
														style={{cursor: "pointer"}}
														title="Edit data"
														onClick={() =>
															history.push(
																"/admission/appointment/edit/" + row.original.id,
															)
														}>
														Edit Data
													</a>
												</li>
											</>
										)}
									<li>
										<a
											class="dropdown-item text-table-medium"
											data-placement="top"
											data-toggle="tooltip"
											style={{cursor: "pointer"}}
											title="Edit data"
											onClick={() =>
												history.push("/admission/appointment/detail/" + row.original.id)
											}>
											Detail
										</a>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
		],
		[showActionMobile],
	);

	// eslint-disable-next-line no-unused-vars
	const dataWithIDSerial = useMemo(() => {
		return (
			appoitments?.data?.rows?.map((item, index) => ({
				...item,
				idSerial: (filter.page - 1) * filter.limit + index + 1,
			})) || []
		);
	}, [appoitments, filter.limit, filter.page]);

	const handleCloseCheckInModal = () => {
		// setSelectedData(null);
		setShowCheckInModal(false);
		setShowActionMobile(false);
	};

	const copyToClipboard = async text => {
		try {
			await navigator.clipboard.writeText(text);
			showToast("success", `Link pembayaran berhasil disalin ke clipboard`, 2000);
		} catch (err) {
			console.error("Gagal menyalin:", err);
		}
	};

	const handleSort = value => {
		const {id, desc} = value?.[0] || {};
		const sortValue = id ? `${id}.${desc ? "desc" : "asc"}` : "createdAt.desc";

		setFilter(prev => ({...prev, sortBy: sortValue}));
	};

	return (
		<>
			{selectedData && (
				<ModalConfirmation
					approveLabel="Ya, lanjutkan"
					description={`Apakah Anda yakin ingin mengubah status pasien "${selectedData.patient.fullName}"? menjadi Check-in?`}
					loading={updateAppointmentMutation.isLoading}
					rejectLabel="Batal"
					visible={showCheckInModal}
					onApprove={handleCheckInAppointment}
					onClose={handleCloseCheckInModal}
					onReject={handleCloseCheckInModal}
				/>
			)}

			{selectedData && (
				<ModalConfirmation
					approveLabel="Ya, lanjutkan"
					description={`Apakah Anda yakin ingin cancel status pasien "${selectedData.patient.fullName}"?`}
					loading={isLoadingCancel}
					rejectLabel="Batal"
					visible={isOpenCancel}
					onApprove={handleCancel}
					onClose={() => setIsOpenCancel(false)}
					onReject={() => setIsOpenCancel(false)}
				/>
			)}

			{selectedData && (
				<ModalConfirmation
					approveLabel="Ya, lanjutkan"
					description={`Apakah Anda yakin ingin mengubah status pasien "${selectedData.patient.fullName}" menjadi sudah bayar?`}
					loading={isLoadingPaid}
					rejectLabel="Batal"
					visible={isOpenPaid}
					onApprove={handlePaid}
					onClose={() => setIsOpenPaid(false)}
					onReject={() => setIsOpenPaid(false)}
				/>
			)}

			{selectedData && (
				<ModalConfirmation
					approveLabel="Kirim"
					description={`Silahkan pilih metode kirim link pembayaran?`}
					loading={isLoadingSharePaymentLink}
					rejectLabel="Batal"
					visible={isOpenSharePaymentLink}
					onApprove={handleSharePaymentLink}
					onClose={() => setIsOpenSharePaymentLink(false)}
					onReject={() => setIsOpenSharePaymentLink(false)}>
					<div className="row mt-4">
						<div className="col-6">
							<div className="d-flex gap-2">
								<input
									checked={shareTo.includes("email")}
									type="checkbox"
									onChange={() => changeShareTo("email")}
								/>{" "}
								<label>Email</label>
							</div>
							<div className="d-flex gap-2 mt-4">
								<input
									checked={shareTo.includes("whatsapp")}
									type="checkbox"
									onChange={() => changeShareTo("whatsapp")}
								/>{" "}
								<label>Whatsapp</label>
							</div>
						</div>
						<div className="col-6 align-items-center">
							<input
								className="border border-secondary-subtle p-2 rounded-3"
								value={selectedData.transaction.paymentUrl}
							/>
							<MdContentCopy
								className="ms-2"
								fontSize={24}
								style={{cursor: "pointer"}}
								title="Copy Link"
								onClick={() => {
									copyToClipboard(selectedData.transaction.paymentUrl);
									setIsOpenSharePaymentLink(false);
								}}
							/>
						</div>
					</div>
				</ModalConfirmation>
			)}

			<DashboardLayout>
				<div class="text-bold text-dark mb-4">List Appointments</div>
				<SearchBox
					rightElement={
						<div className="row position-relative search-hospital-container align-items-center justify-content-end">
							<div className="col-lg-6 col-sm-12">
								<ButtonAddTable
									text="Tambah Appointment"
									onClick={() => history.push("/admission/appointment/add")}
								/>

								<div className="mt-3 form-filter position-relative">
									<div className="form-filter position-relative">
										<DatePicker
											singleDatePicker
											onChange={selectedDate => {
												setFilter({
													...filter,
													date: dayjs(selectedDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
												});
											}}>
											<input
												className="form-control"
												placeholder="Pilih tanggal"
												type="text"
												value={
													(filter.date &&
														dayjs(filter.date, "YYYY-MM-DD").format("DD MMMM YYYY")) ||
													""
												}
											/>
										</DatePicker>
										<div className="icon-right">
											<span className="icon-ico-date text-orange" />
										</div>
									</div>
								</div>
							</div>
						</div>
					}
					onSearch={keyword => {
						setFilter({...filter, search: keyword, page: 1});
					}}
				/>
				<BasicTable
					columns={isMobile ? columnsMobile : columns}
					currentPage={filter.page}
					data={dataWithIDSerial}
					disableSortBy={false}
					handleSort={value => handleSort(value)}
					limit={filter.limit}
					loading={isLoading}
					striped={true}
					title="Data File Laboratorium"
					totalItems={appoitments?.data?.totalItems}
					totalPage={appoitments?.data?.totalPages}
					onLimitChange={limitValue => {
						setFilter({...filter, limit: limitValue, page: 1});
					}}
					onPageChange={selectedPage => {
						setFilter({...filter, page: selectedPage});
					}}
				/>
			</DashboardLayout>
		</>
	);
}
